import {
	Button,
	Modal,
	Space,
	Table,
	TableColumnsType,
	Typography,
} from 'antd';
import { useRolesWithModulesList } from './useRolesWithModulesList';
import CompleteCheck from '../../../assets/images/complete-check.svg';
import InCompleteCheck from '../../../assets/images/Incomplete-check.svg';
import useUserSession from '../../../hooks/useUserSession';
import { useCallback, useEffect, useState } from 'react';
import { useBatchId } from '../../../hooks/useBatchId';
import { MANAGE_PRIVILEGES, SELECT_MODULE } from '../../../shared/Routes';
import { UpdateRoleModules } from './UpdateRoleModules';
import { displayError } from '../../../shared/Utils';
import { useToastMessage } from '../../../hooks/useToastMessage';
import Icon from '../../../components/ui-components/Icon';
import LeftArrowWhite from '../../../assets/images/arrow-left-white.svg';
import {
	RoleBasedModules,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';

const { Text } = Typography;

interface TableRow {
	key: string;
	roleName: string;
	roleCode: string;
}

export const RolesWithModulesList = () => {
	const { showToastMessage } = useToastMessage();
	const { allRoles, allModules, updateModules, refreshRoleModules } =
		useRolesWithModulesList();
	const { allRolesWithModules: sessionRolesWithModules } = useUserSession();
	const [rolesWithModules, setRolesWithModules] = useState(
		sessionRolesWithModules
	);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [roleToUpdate, setRoleToUpdate] = useState<string>();
	const [roleNameToUpdate, setRoleNameToUpdate] = useState<string>();
	const { navigateToRoute } = useBatchId();
	const accessMap = new Map<string, Set<string>>();
	const excludedRoles = [
		UserRole.SNP_ADMIN,
		UserRole.PRECEPTOR,
		UserRole.NPS_MENTOR,
		UserRole.PREWORK_MENTOR,
		UserRole.CARE_TEAM,
	];

	const filteredRolesWithModules =
		rolesWithModules?.filter(
			({ role }) => !excludedRoles.includes(role.code as UserRole)
		) || [];

	const filteredRoles = allRoles.filter(
		(role) => !excludedRoles.includes(role.code as UserRole)
	);

	filteredRolesWithModules.forEach((item) => {
		accessMap.set(
			item.role.code,
			new Set(
				item?.modules
					?.filter((m) => m?.roleModuleMappingStatus === 'ACTIVE')
					?.map((m) => m.module?.code) ?? []
			)
		);
	});

	useEffect(() => {
		setRolesWithModules(sessionRolesWithModules);
	}, [sessionRolesWithModules]);

	const openUpdateModulesModal = (record: TableRow) => {
		setRoleToUpdate(record.roleCode);
		setRoleNameToUpdate(record.roleName);
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
	};

	const handleUpdatePrivileges = (record: TableRow) => {
		if (record.roleCode === UserRole.SNP_ADMIN) return;

		const activeModules =
			filteredRolesWithModules
				?.find((r) => r.role.code === record.roleCode)
				?.modules.filter((m) => m.roleModuleMappingStatus === 'ACTIVE')
				.map((m) => ({
					id: m.module.id,
					name: m.module.name,
				})) || [];

		navigateToRoute(MANAGE_PRIVILEGES, {
			state: {
				roleId: record.key,
				roleCode: record.roleCode,
				roleName: record.roleName,
				modules: activeModules,
			},
		});
	};

	rolesWithModules?.forEach((item) => {
		accessMap.set(
			item.role.code,
			new Set(
				item.modules
					.filter((m) => m.roleModuleMappingStatus === 'ACTIVE')
					.map((m) => m.module.code)
			)
		);
	});

	const columns: TableColumnsType<TableRow> = [
		{
			title: 'Roles',
			dataIndex: 'roleName',
			key: 'roleName',
			fixed: 'left' as const,
			width: 220,
		},

		...allModules
			.filter(
				(module) =>
					module.code !== RoleBasedModules.MANAGE_PRIVILEGES_MODULE &&
					module.code !== RoleBasedModules.MANAGE_FUNCTIONARIES_MODULE
			)
			.map((module) => ({
				title: module.name,
				key: module.code,
				align: 'center' as const,

				render: (_: unknown, record: TableRow) => {
					if (
						record.roleCode === UserRole.PREWORK_MENTOR &&
						module.code === RoleBasedModules.SCREENING_MODULE
					) {
						return null;
					}

					// Hide NPS & Prework Module for Care Team
					if (
						(record.roleCode === UserRole.NPS_ADMIN ||
							record.roleCode === UserRole.PREWORK_ADMIN) &&
						module.code === RoleBasedModules.SCREENING_MODULE
					) {
						return null;
					}
					const hasAccess =
						accessMap.get(record.roleCode)?.has(module.code) || false;

					return hasAccess ? (
						<img
							src={CompleteCheck}
							alt="Complete"
							className="w-5 h-5 mx-auto"
						/>
					) : (
						<img
							src={InCompleteCheck}
							alt="Incomplete"
							className="w-5 h-5 mx-auto"
						/>
					);
				},
			})),

		{
			title: 'Actions',
			key: 'actions',
			fixed: 'right',
			width: 250,

			render: (_: unknown, record: TableRow) => (
				<Space>
					<Button onClick={() => openUpdateModulesModal(record)}>
						Update Modules
					</Button>

					<Button type="primary" onClick={() => handleUpdatePrivileges(record)}>
						Update Privileges
					</Button>
				</Space>
			),
		},
	];

	const dataSource: TableRow[] = filteredRoles.map((role) => ({
		key: role.id.toString(),
		roleName: role.name,
		roleCode: role.code,
	}));

	const handleUpdateModules = async (payload: {
		roleCode: string;
		moduleIds: number[];
	}) => {
		try {
			const role = allRoles.find((r) => r.code === payload.roleCode);

			if (!role) {
				throw new Error('Role not found');
			}

			await updateModules(role.id, payload.moduleIds);

			Modal.success({
				title: 'Modules Updated',
				content: 'Modules have been successfully updated.',
				centered: true,
				okText: 'OK',
				okButtonProps: { type: 'primary', className: 'bg-primary-blue' },
				onOk() {},
			});

			const updateRoleMappings = await refreshRoleModules();
			if (updateRoleMappings) {
				setRolesWithModules(updateRoleMappings);
			}
		} catch (error) {
			const message = displayError(error);
			showToastMessage('error', message);
		}
	};

	const handleBackClick = useCallback(() => {
		navigateToRoute(SELECT_MODULE);
	}, [navigateToRoute]);

	return (
		<div className="p-8 bg-gray-50 min-h-screen">
			<div className="bg-white p-6 rounded-xl shadow-sm border">
				<div className="flex gap-6 items-center">
					<Button
						type="primary"
						className="hidden md:block font-bold text-sm mt-5"
						onClick={handleBackClick}
					>
						<span className="flex gap-2">
							<Icon src={LeftArrowWhite} width="w-4" height="h-4" />
							Back
						</span>
					</Button>
					<Text className="font-sans font-bold text-2xl text-primary-blue mt-4">
						Roles & Modules Access List
					</Text>
				</div>

				<Table
					columns={columns}
					dataSource={dataSource}
					pagination={false}
					scroll={{ x: 'max-content' }}
					bordered
					className="mt-4"
				/>

				<UpdateRoleModules
					open={isModalOpen}
					onClose={closeModal}
					modules={allModules}
					roleCode={roleToUpdate}
					roleName={roleNameToUpdate}
					allRolesWithModules={rolesWithModules}
					onSave={handleUpdateModules}
				/>
			</div>
		</div>
	);
};
